import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import type { IntlShape } from 'react-intl';

import { Button } from '~/eds/src/components/Button/Button';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Select } from '~/eds/src/components/Select/Select';
import { Option } from '~/eds/src/types/Option';
import { Nullable } from '~/types/Nullable';
import { sortByStringValue } from '~/utils/array';

import { ClauseType } from './types';

interface Props {
  clauseTypes: ClauseType[];
  isLoading?: boolean;
  onSubmit: (clauseType: ClauseType) => void;
}

export const ClauseTypeSelect = ({
  clauseTypes,
  isLoading,
  onSubmit,
}: Props) => {
  const intl = useIntl();
  const [value, setValue] = useState<Nullable<ClauseType>>(null);

  const options = transformClauseTypeOptions(clauseTypes, intl);
  const clauseTypeSelectProps = {
    options,
    isLoading,
    isMulti: false,
  };
  return (
    <Layout preset="form-fields">
      <FormField<ClauseType, false>
        name="clause type select"
        label={intl.formatMessage({
          id: 'ClauseTypeSelect.selectClauseTypeLabel',
          defaultMessage: 'Select a Clause Type',
          description: 'Label for clause type selection field',
        })}
        placeholder={intl.formatMessage({
          id: 'ClauseTypeSelect.searchPlaceholder',
          defaultMessage: 'Search and select...',
          description: 'Placeholder text for clause type search input',
        })}
        input={Select}
        inputProps={clauseTypeSelectProps}
        value={value}
        onChange={setValue}
      />
      <Button
        disabled={!value}
        icon="status-new"
        iconPosition="left"
        text={intl.formatMessage({
          id: 'ClauseTypeSelect.generateSuggestionsButton',
          defaultMessage: 'Generate Suggestions',
          description: 'Button text to generate clause suggestions',
        })}
        variant="primary"
        onClick={() => onSubmit(value!)}
      />
    </Layout>
  );
};

export const transformClauseTypeOptions = (
  clauseTypes: ClauseType[],
  intl: IntlShape,
): Option<ClauseType, false>[] => {
  return clauseTypes
    .map((clauseType) => ({
      label: clauseType.name,
      value: clauseType,
      description: intl.formatMessage(
        {
          id: 'ClauseTypeSelect.contractCountDescription',
          defaultMessage: 'Appears in {count} contracts',
          description:
            'Description showing how many contracts contain this clause type. Example: count = "150"',
        },
        { count: clauseType.count },
      ),
    }))
    .sort(sortByStringValue('label'));
};
